// External Libraries
import * as React from "react";
import { formatText, formatDateString } from "bernie-l10n";
import TruncatedText from "shiitake";

// Types
import { Hotel } from "typings/microserviceModels/hotels-flex-module";

// UITK
import { UitkPriceLockup } from "uitk-react-price-lockup";

// Components
import { ReviewCountAndRating } from "components/flexComponents/Hotels/components/ReviewCountAndRating";
import { showReviewCountAndRating } from "components/flexComponents/Hotels/components/FeaturedReviewContent";
import { useHotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";

// Utils
import { getAddress } from "components/flexComponents/Hotels/utils/getAddress";
import { validPrice } from "components/flexComponents/Hotels/utils/isValidPrice";
import { DATE_FORMAT } from "components/flexComponents/Hotels/utils/constants";

interface SummarizedContentProps {
  hotel: Hotel;
  isMobile: boolean;
  showCheckPrices?: boolean;
}

export const SummarizedContent: React.FC<SummarizedContentProps> = ({ hotel, isMobile, showCheckPrices }) => {
  const {
    hotelName,
    formattedPrice,
    isoCheckIn,
    isoCheckOut,
    reviewCount,
    hotelOverallReviewRating,
    featuredReviews,
  } = hotel;
  const {
    context,
    flexComponent: {
      model: { showGetPriceIfNoPrice, showGuestRating },
    },
  } = useHotelsContext();

  const { isValidPrice } = validPrice(hotel, isMobile, showCheckPrices, showGetPriceIfNoPrice);

  const { checkInDateFormatted, checkOutDateFormatted } = React.useMemo(
    () => ({
      checkInDateFormatted: formatDateString(isoCheckIn, { skeleton: DATE_FORMAT }),
      checkOutDateFormatted: formatDateString(isoCheckOut, { skeleton: DATE_FORMAT }),
    }),
    [isoCheckIn, isoCheckOut]
  );

  const language = context.locale?.split(/[_-]/);

  return (
    <section className="uitk-card-text-segment uitk-flex uitk-flex-column uitk-flex-justify-content-space-between">
      <div>
        <div className="uitk-grid all-x-gutter-two">
          <div className="uitk-cell all-cell-fill">
            <div className="uitk-type-600 uitk-type-bold all-b-padding-one">
              <span className="uitk-type-heading-500" data-testid="hotel-name">
                <TruncatedText renderFullOnServer={true} lines={2}>
                  {hotelName}
                </TruncatedText>
              </span>
            </div>
            <div className="uitk-type-300 all-b-padding-one">{getAddress(hotel)}</div>
          </div>
          {isValidPrice && (
            <UitkPriceLockup
              className="uitk-cell all-cell-shrink"
              lockupPrice={formattedPrice}
              priceCaptionFirst={formatText("hotels.price.perNight")}
              priceCaptionSecond={formatText("hotels.whenBooking", checkInDateFormatted, checkOutDateFormatted)}
            />
          )}
        </div>
      </div>
      {showReviewCountAndRating(showGuestRating, reviewCount, hotelOverallReviewRating, featuredReviews) && (
        <ReviewCountAndRating
          hotelOverallReviewRating={hotelOverallReviewRating}
          reviewCount={reviewCount}
          language={language}
        />
      )}
    </section>
  );
};
